import React, { useMemo, useState, useEffect } from 'react';
import { Button, SpaceBetween, StatusIndicator, Alert } from '@amzn/awsui-components-core';
import { updateDasFinSumFeedbackMutation } from 'src/api/app-sync-services';
import { useEventsApiContext } from 'src/components/context/EventsApiContextProvider';
import styles from './ai-summary-components.module.scss';

export type FeedbackStatus = 'idle' | 'submitting' | 'success' | 'error';
export type FeedbackType = 'thumbs-up' | 'thumbs-down';

const CTI_LINK = 'https://t.corp.amazon.com/create/templates/2d0edec5-6161-4e70-9f6b-7822f2545c2d';

interface FeedbackButtonsProps {
  summaryId: string;
  userId: string;
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ summaryId, userId }) => {
  const eventsApiContext = useEventsApiContext();
  // Get existing feedback from context
  const cachedEntry = eventsApiContext.getCacheEntry(summaryId);
  const existingFeedback = cachedEntry?.feedbackType;
  // Local state for feedback status
  const [feedbackStatus, setFeedbackStatus] = useState<FeedbackStatus>('idle');
  const selectedFeedback = existingFeedback;
  const [displayCti, setDisplayCti] = useState<boolean>(false);

  useEffect(() => {
    if (selectedFeedback === 'thumbs-down') {
      setDisplayCti(true);
    } else {
      setDisplayCti(false);
    }
  }, [selectedFeedback]);

  const handleFeedback = async (feedbackType: FeedbackType) => {
    if (!summaryId || !userId || feedbackStatus === 'submitting') return;

    try {
      setFeedbackStatus('submitting');
      const feedbackValue = feedbackType === 'thumbs-up' ? 1 : -1;
      const response = await updateDasFinSumFeedbackMutation({
        userId,
        summaryId,
        feedback: feedbackValue
      });
      if (response.updateDasFinSumFeedback === true) {
        setFeedbackStatus('success');
        eventsApiContext.updateFeedback(summaryId, feedbackType);
      } else {
        throw new Error('Failed to record feedback');
      }
    } catch (error) {
      console.error(`Error submitting ${feedbackType} feedback:`, error);
      setFeedbackStatus('error');
    }
  };

  if (feedbackStatus === 'submitting') {
    return <StatusIndicator type="loading">Thanks for sharing the feedback.</StatusIndicator>;
  }

  if (feedbackStatus === 'error') {
    return (
      <div className={styles.feedbackAlert}>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Alert dismissible type="error" onDismiss={() => setFeedbackStatus('idle')}>
            Feedback submission failed. Please try again.
          </Alert>
        </SpaceBetween>
      </div>
    );
  }

  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <Button
        variant="icon"
        iconName={selectedFeedback === 'thumbs-up' ? 'thumbs-up-filled' : 'thumbs-up'}
        onClick={() => handleFeedback('thumbs-up')}
        ariaLabel="Thumbs up"
      />
      <Button
        variant="icon"
        iconName={selectedFeedback === 'thumbs-down' ? 'thumbs-down-filled' : 'thumbs-down'}
        onClick={() => handleFeedback('thumbs-down')}
        ariaLabel="Thumbs down"
      />
      {displayCti && (
        <a href={CTI_LINK} target="_blank" rel="noopener noreferrer" aria-label="Report an issue with this AI summary">
          Report an issue?
        </a>
      )}
    </SpaceBetween>
  );
};
